import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import TopicHeader from "@/pages/topic/components/TopicHeader";
import SideCompare from "@/pages/topic/components/SideCompare";
import CommentSection from "@/pages/topic/components/CommentSection";
import RelatedSidebar from "@/pages/topic/components/RelatedSidebar";
import useSeo from "@/hooks/useSeo";
import { topics } from "@/mocks/home";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((item) => item.id === id);

  useSeo({
    title: topic
      ? `${topic.title} - 网爆黑料话题站队投票 - 吃瓜瓜田`
      : "话题不存在 - 吃瓜瓜田",
    description: topic
      ? `${topic.summary} 参与网爆黑料站队投票与观点PK，看看其他吃瓜群众怎么选。`
      : "该话题可能已被合并或调整，请返回瓜田热榜继续吃瓜。",
    keywords: topic
      ? `${topic.categoryName},网爆黑料,站队投票,观点PK,二创解读,${topic.tags.join(",")}`
      : "网爆黑料,吃瓜群众",
    canonicalPath: `/topic/${id ?? ""}`,
    ogType: "article",
  });

  if (!topic) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1400px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-600">
            <i className="ri-emotion-sad-line text-3xl" aria-hidden="true" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            这个瓜暂时找不到了
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            话题可能已经被合并或调整。回到瓜田热榜，还有一堆新鲜话题在等你。
          </p>
          <Link
            to="/"
            className="mt-8 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line text-base" aria-hidden="true" />
            返回瓜田热榜
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: topic.title,
    description: topic.summary,
    inLanguage: "zh-CN",
    articleSection: topic.categoryName,
    keywords: topic.tags.join(","),
    timeRequired: "PT5M",
    publisher: { "@type": "Organization", name: "吃瓜瓜田" },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://guatian-community.example/" },
      { "@type": "ListItem", position: 2, name: "瓜田热榜", item: "https://guatian-community.example/#topics" },
      { "@type": "ListItem", position: 3, name: topic.title },
    ],
  };

  return (
    <div id="top" className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="w-full flex-1">
        <TopicHeader topic={topic} />

        <section aria-labelledby="compare-title" className="w-full bg-background-50 py-12 md:py-16">
          <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-10 px-4 md:px-8 lg:grid-cols-[1.5fr_0.5fr] lg:gap-12">
            <SideCompare sideA={topic.sideA} sideB={topic.sideB} />
            <RelatedSidebar />
          </div>
        </section>

        <CommentSection sideA={topic.sideA} sideB={topic.sideB} />

        <section className="w-full bg-background-50 py-12 md:py-14">
          <div className="mx-auto w-full max-w-[1400px] px-4 md:px-8">
            <div className="flex flex-col items-start justify-between gap-5 rounded-lg border border-background-200 bg-background-100/60 p-6 md:flex-row md:items-center md:p-8">
              <div>
                <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                  吃瓜也要有分寸
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  本站话题均为抽象虚构演绎，不指向任何真实个人。讨论请基于公开信息，不要人肉、不要造谣、不要人身攻击。
                </p>
              </div>
              <Link
                to="/#rules"
                className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-shield-check-line text-base" aria-hidden="true" />
                查看吃瓜守则
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </div>
  );
}